import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { fetchAndSaveUserDetails } from "../services/ApiService";
import { unblockUser } from "../services/handleBlockingUsers";
import { getSwishPaymentStatus } from "../services/swishService";
import swishImage from "../assets/images/swish.png";

const SWISH_BASE_URL = "https://boxmealadminapinew.azurewebsites.net/api/ShoppingCart";

const BlockedTemporaryPage = () => {
    const [unblockingAmount, setUnblockingAmount] = useState(null);
    const [isPaying, setIsPaying] = useState(false);
    const [error, setError] = useState(null);
    const intervalId = useRef(null);

    const { user, logout, token } = useAuth();
    const navigate = useNavigate();


    useEffect(() => {
        const interval = setInterval(async () => {
            try {
                const res = await fetch("https://boxmealadminapinew.azurewebsites.net/api/auth/details", {
                    method: "GET",
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) return;
                const data = await res.json();
                if (data && data.isBlocked === false) {
                    logout();
                    navigate("/", { replace: true });
                }
            } catch {}
        }, 30000);

        return () => clearInterval(interval);
    }, [logout, navigate, token]);


    useEffect(() => {
        async function loadDetails() {
            try {
                const details = await fetchAndSaveUserDetails(token);
                if (details && typeof details.unblockingAmount === "number") {
                    setUnblockingAmount(details.unblockingAmount);
                }
            } catch {}
        }
        loadDetails();
    }, [token]);

    const handleSwishClick = async () => {
        setIsPaying(true);
        setError(null);
        try {
            const res = await fetch(`${SWISH_BASE_URL}/initiate`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    userId: user.id,
                    payeePaymentReference: "1235324876",
                    callbackUrl: `${SWISH_BASE_URL}/callback`,
                    payeeAlias: "1235324876",
                    amount: unblockingAmount,
                    currency: "SEK",
                    message: "Upplåsning av konto",
                    items: [],
                }),
            });

            if (!res.ok) throw new Error("Misslyckades att initiera Swish-betalning");
            const data = await res.json();

            // Open Swish app on phone
            const swishUrl = `swish://paymentrequest?token=${encodeURIComponent(data.token)}`;
            setTimeout(() => {
                window.location.assign(swishUrl);
            }, 500);

            // Start polling Swish payment
            startPollingPaymentStatus(data.id);

        } catch (e) {
            setError(e.message || "Kunde inte initiera Swish.");
            setIsPaying(false);
        }
    };

    const startPollingPaymentStatus = (paymentId) => {
        if (intervalId.current) return; // Prevent multiple intervals
        intervalId.current = setInterval(async () => {
            try {
                const statusResponse = await getSwishPaymentStatus(paymentId);

                if (statusResponse.status === "PAID") {
                    clearInterval(intervalId.current);
                    intervalId.current = null;

                    // Unblock user after Swish paid
                    const result = await unblockUser(user.id, "Betalt via Swish", token);
                    if (result.success) {
                        alert("Betalning mottagen. Ditt konto är nu upplåst!");
                        logout();
                        navigate("/", { replace: true });
                    } else {
                        setError("Betalning ok, men upplåsning misslyckades.");
                        setIsPaying(false);
                    }
                } else if (
                    ["CANCELLED", "DECLINED", "ERROR"].includes(statusResponse.status)
                ) {
                    clearInterval(intervalId.current);
                    intervalId.current = null;
                    setError("Betalningen misslyckades. Försök igen.");
                    setIsPaying(false);
                }
            } catch (error) {
                clearInterval(intervalId.current);
                intervalId.current = null;
                setError("Fel vid kontroll av Swish-status.");
                setIsPaying(false);
            }
        }, 2000);
    };

    useEffect(() => {
        return () => {
            if (intervalId.current) clearInterval(intervalId.current);
        };
    }, []);

    const handleContactClick = () => {
        window.location.href = "mailto:support@ziplink.se";
    };

    return (
        <Wrapper>
            <Centered>
                <Title>Hoppsan! Det gick visst lite snabbt sist..</Title>
                <Description>
                    Vårt AI-system har detekterat en avvikelse när du handlat ur en av våra enheter.
                </Description>
                <Description>
                    Innan du kan göra ett nytt köp behöver du komplettera en tidigare köporder. Vänligen se vårt mejl för ytterligare information. Om du inte fått något mejl, vänligen kolla skräpposten.
                </Description>

                <SwishLogo src={swishImage} alt="Swish" />

                <SwishButton
                    onClick={handleSwishClick}
                    disabled={isPaying || !unblockingAmount}
                >
                    {isPaying ? "Vänta på betalning..." : `Betala ${unblockingAmount || ""} kr med Swish`}
                </SwishButton>

                {error && <ErrorText>{error}</ErrorText>}
                <ContactRow>
                    <ContactText onClick={handleContactClick}>
                        Det har blivit fel, kontakta oss
                    </ContactText>
                </ContactRow>
            </Centered>

        </Wrapper>
    );
};

export default BlockedTemporaryPage;

const Wrapper = styled.div`
  background: #fff;
  min-height: 100vh;
  width: 100vw;
  display: flex;
  justify-content: center;
  align-items: flex-start;
`;
const Centered = styled.div`
    margin: 0 auto;
    padding: 0 24px;
    max-width: 520px;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 90px;
`;
const Title = styled.h1`
    text-align: center;
    font-size: 0.8rem;
    font-weight: 700;
    margin-bottom: 26px;
`;
const Description = styled.p`
    text-align: center;
    font-size: 0.8rem;
    color: #222;
    margin-bottom: 18px;
    margin-top: 0;
    line-height: 1.4;
`;
const SwishButton = styled.button`
    width: 100%;
    padding: 15px 0;
    margin: 0 auto 10px auto;
    border-radius: 999px;
    border: 2px solid #111;
    background: #fff;
    color: #111;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 1px 10px 0 rgba(16,24,40,0.04);
    transition: background 0.17s, color 0.17s, border 0.17s, box-shadow 0.17s;
    &:hover, &:focus {
        background: #111;
        color: #fff;
        border: 2px solid #111;
        box-shadow: 0 2px 24px 0 rgba(16,24,40,0.10);
        outline: none;
    }
`;


const SwishLogo = styled.img`
    //width: 50px;
    height: 50px;
    display: block;
    margin: 0 auto 18px auto;
`;


const ContactRow = styled.div`
    width: 100%;
    text-align: center;
`;
const ContactText = styled.span`
    font-size: 1.11rem;
    color: #111;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;
    transition: color 0.15s;
    margin-top: 20px;
    &:hover {
        color: #555;
        text-decoration: underline;
    }
`;
const ErrorText = styled.div`
    color: #c00;
    background: #fff4f4;
    font-size: 0.93rem;
    border-radius: 6px;
    margin: 12px 0 0 0;
    padding: 8px 12px;
    width: 100%;
    text-align: center;
`;

